import type { FilterDefinitionType, FilterType } from "@/components/@types";
import {
  buildWidgetFilterSet,
  type WidgetFilterSets,
} from "@/app/app/_/views/ReportsView/reportFilters";
import {
  getDefaultReportsFilters,
  type ReportLayoutWidget,
  type PresentationKind,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { sharedFilterDefinitionsToRecord } from "@/app/share/report/sharedFilterDefinitions";
import { normalizeFilterOperator } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import {
  reportLayoutWidgetToSharedShell,
  sharedReportToDefinition,
} from "@/app/share/report/sharedReportToDefinition";
import type {
  SharedFilterOutput,
  SharedViewLayout,
  SharedViewWidgetShell,
} from "@/app/share/report/sharedReportTypes";

export type SharedEntityOption = {
  identifier: string;
  name: string;
};

/** Layout-level filters in the report subnav — not shown on individual widgets. */
export const SHARED_LAYOUT_GLOBAL_FILTER_KEYS = new Set([
  "dates",
  "frequency",
  "entities",
]);

export function buildSharedEntityOptions(
  layout: SharedViewLayout,
): SharedEntityOption[] {
  const ids = layout.defaultEntityIdentifiers ?? [];
  const names = layout.entityNames ?? [];
  return ids.map((identifier, index) => ({
    identifier,
    name: names[index] ?? identifier,
  }));
}

export function sharedLayoutHasMultipleEntities(
  layout: SharedViewLayout,
): boolean {
  return buildSharedEntityOptions(layout).length > 1;
}

function sharedTypeToFilterType(type: string): FilterType["type"] {
  switch (type.toUpperCase()) {
    case "DATE":
      return "date";
    case "ENUM":
      return "enum";
    case "NUMBER":
      return "number";
    case "TEXT":
      return "text";
    default:
      return "text";
  }
}

export function sharedFilterIsLocked(
  filter: Pick<SharedFilterOutput, "isLocked"> & { is_locked?: boolean },
): boolean {
  return filter.isLocked === true || filter.is_locked === true;
}

export function sharedFilterOutputToFilterType(
  filter: SharedFilterOutput,
): FilterType {
  return {
    key: filter.key,
    type: sharedTypeToFilterType(filter.type),
    operator: normalizeFilterOperator(
      filter.operator as FilterType["operator"],
    ),
    value: filter.value,
    ...(sharedFilterIsLocked(filter) ? { isLocked: true } : {}),
  } as FilterType;
}

/** Widget shells derived from the layout's canonical definition JSON. */
export function allSharedLayoutWidgets(
  layout: SharedViewLayout,
): SharedViewWidgetShell[] {
  const definition = sharedReportToDefinition(layout);
  return definition.widgets.map((widget) =>
    reportLayoutWidgetToSharedShell(widget),
  );
}

/** Global filter defaults aggregated from definition widget ``defaultFilters``. */
export function resolveSharedGlobalFilterDefaults(
  layout: SharedViewLayout,
): SharedFilterOutput[] {
  const byKey = new Map<string, SharedFilterOutput>();

  for (const widget of allSharedLayoutWidgets(layout)) {
    for (const filter of widget.defaultFilters ?? []) {
      if (!SHARED_LAYOUT_GLOBAL_FILTER_KEYS.has(filter.key)) {
        continue;
      }
      const existing = byKey.get(filter.key);
      if (!existing) {
        byKey.set(filter.key, filter);
        continue;
      }
      if (sharedFilterIsLocked(filter) && !sharedFilterIsLocked(existing)) {
        byKey.set(filter.key, filter);
      }
    }
  }

  return [...byKey.values()];
}

export function initialSharedGlobalFilters(
  layout: SharedViewLayout,
): FilterType[] {
  const entities = buildSharedEntityOptions(layout);
  const publishedByKey = new Map(
    resolveSharedGlobalFilterDefaults(layout).map((filter) => [
      filter.key,
      filter,
    ]),
  );

  const filters: FilterType[] = getDefaultReportsFilters()
    .filter((filter) => filter.key !== "entities")
    .map((fallback) => {
      const published = publishedByKey.get(fallback.key);
      return published ? sharedFilterOutputToFilterType(published) : fallback;
    });

  if (sharedLayoutHasMultipleEntities(layout)) {
    const publishedEntities = publishedByKey.get("entities");
    filters.push(
      publishedEntities
        ? sharedFilterOutputToFilterType(publishedEntities)
        : ({
            key: "entities",
            type: "enum",
            operator: "inList",
            value: entities.map((entity) => entity.identifier),
          } as FilterType),
    );
  }

  return filters;
}

export function editableSharedFilters(filters: FilterType[]): FilterType[] {
  return filters.filter((filter) => filter.isLocked !== true);
}

export function editableSharedGlobalFilterKeys(
  layout: SharedViewLayout,
): Set<string> {
  const published = resolveSharedGlobalFilterDefaults(layout);
  const keys = new Set<string>();

  if (published.length > 0) {
    for (const filter of published) {
      if (!sharedFilterIsLocked(filter)) {
        keys.add(filter.key);
      }
    }
  } else {
    for (const filter of initialSharedGlobalFilters(layout)) {
      if (filter.isLocked !== true) {
        keys.add(filter.key);
      }
    }
  }

  // Layout entity scope is first-class on the report; expose a global entities
  // filter whenever the publisher selected more than one entity.
  if (sharedLayoutHasMultipleEntities(layout)) {
    const publishedEntities = published.find(
      (filter) => filter.key === "entities",
    );
    if (!publishedEntities || !sharedFilterIsLocked(publishedEntities)) {
      keys.add("entities");
    }
  }

  return keys;
}

export function hasEditableSharedGlobalFilters(
  layout: SharedViewLayout,
): boolean {
  return editableSharedGlobalFilterKeys(layout).size > 0;
}

export function mergeEditableSharedFilterChanges(
  current: FilterType[],
  nextEditable: FilterType[],
): FilterType[] {
  const locked = current.filter((filter) => filter.isLocked === true);
  const lockedKeys = new Set(locked.map((filter) => filter.key));
  return [
    ...locked,
    ...nextEditable.filter((filter) => !lockedKeys.has(filter.key)),
  ];
}

function enrichSharedWidgetShell(
  widget: SharedViewWidgetShell,
  reportLayoutWidget?: ReportLayoutWidget,
): SharedViewWidgetShell {
  const byKey = new Map<string, SharedFilterOutput>();

  for (const filter of reportLayoutWidget?.defaultFilters ?? []) {
    byKey.set(filter.key, {
      key: filter.key,
      type: filter.type,
      operator: filter.operator,
      value: filter.value,
      isLocked: filter.isLocked ?? false,
    });
  }

  for (const filter of widget.defaultFilters ?? []) {
    byKey.set(filter.key, filter);
  }

  return { ...widget, defaultFilters: [...byKey.values()] };
}

function sharedFilterHasValue(filter: FilterType): boolean {
  return filter.operator != null && filter.value != null && filter.value !== "";
}

export function resolveSharedWidgetFilterDefinitions(
  widget: SharedViewWidgetShell,
): Record<string, FilterDefinitionType> {
  const fromApi = sharedFilterDefinitionsToRecord(widget.filterDefinitions);
  return Object.fromEntries(
    Object.entries(fromApi).filter(
      ([key]) => !SHARED_LAYOUT_GLOBAL_FILTER_KEYS.has(key),
    ),
  );
}

export function sharedReportWidgetLocalFilterKeys(
  widget: SharedViewWidgetShell,
): string[] {
  return Object.keys(resolveSharedWidgetFilterDefinitions(widget));
}

export function sharedReportWidgetLocalFilterDefinitions(
  widget: SharedViewWidgetShell,
): Record<string, FilterDefinitionType> {
  return resolveSharedWidgetFilterDefinitions(widget);
}

export function editableSharedWidgetLocalFilterDefinitions(
  widget: SharedViewWidgetShell,
): Record<string, FilterDefinitionType> {
  const all = sharedReportWidgetLocalFilterDefinitions(widget);
  return Object.fromEntries(
    Object.entries(all).filter(([key]) => {
      const stored = widget.defaultFilters?.find(
        (filter) => filter.key === key,
      );
      return !stored || !sharedFilterIsLocked(stored);
    }),
  );
}

export function initialSharedWidgetLocalFilters(
  widget: SharedViewWidgetShell,
): FilterType[] {
  const shell = enrichSharedWidgetShell(widget);
  const localKeys = new Set(sharedReportWidgetLocalFilterKeys(shell));
  return (shell.defaultFilters ?? [])
    .filter((filter) => localKeys.has(filter.key))
    .map(sharedFilterOutputToFilterType);
}

/** Header chips: published widget-local values with construct defaults for editable keys. */
export function resolveSharedWidgetHeaderFilters(
  widget: SharedViewWidgetShell,
  reportLayoutWidget?: ReportLayoutWidget,
): FilterType[] {
  const shell = enrichSharedWidgetShell(widget, reportLayoutWidget);
  const editableKeys = Object.keys(
    editableSharedWidgetLocalFilterDefinitions(shell),
  );
  if (editableKeys.length === 0) {
    return [];
  }

  const published = new Map(
    initialSharedWidgetLocalFilters(shell).map((filter) => [
      filter.key,
      filter,
    ]),
  );

  return editableKeys
    .map((key) => published.get(key))
    .filter(
      (filter): filter is FilterType =>
        filter != null && sharedFilterHasValue(filter),
    );
}

export function resolveSharedWidgetLocalFilters(
  widget: SharedViewWidgetShell,
  overrides?: FilterType[],
  reportLayoutWidget?: ReportLayoutWidget,
): FilterType[] {
  return (
    overrides ?? resolveSharedWidgetHeaderFilters(widget, reportLayoutWidget)
  );
}

export function widgetSupportsLocalFilters(widget: SharedViewWidgetShell): boolean {
  // A widget that summarizes a service's own rows is the one with filters of
  // its own; everything else takes the layout's.
  return Boolean(widget.source) && widget.presentation === "TABLE";
}

function filterTypeToSharedType(type: FilterType["type"]): string {
  switch (type) {
    case "date":
      return "DATE";
    case "enum":
      return "ENUM";
    case "number":
    case "currency":
      return "NUMBER";
    case "text":
      return "TEXT";
    default:
      return String(type).toUpperCase();
  }
}

export function filterTypesToSharedOutputs(
  filters: FilterType[],
): SharedFilterOutput[] {
  return filters
    .filter(
      (filter) =>
        filter.isLocked !== true &&
        filter.key !== "entities" &&
        filter.operator != null &&
        filter.value != null &&
        filter.value !== "",
    )
    .map((filter) => ({
      key: filter.key,
      type: filterTypeToSharedType(filter.type),
      operator: String(filter.operator),
      value: filter.value,
      isLocked: false,
    }));
}

export function mergeSharedFilterOutputs(
  defaults: SharedFilterOutput[],
  ...layers: SharedFilterOutput[][]
): SharedFilterOutput[] {
  const map = new Map<string, SharedFilterOutput>();

  for (const filter of defaults) {
    map.set(filter.key, filter);
  }

  for (const layer of layers) {
    for (const filter of layer) {
      const existing = map.get(filter.key);
      if (existing && sharedFilterIsLocked(existing)) continue;
      map.set(filter.key, filter);
    }
  }

  return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
}

function resolveSharedEntityIdentifiers(
  layout: SharedViewLayout,
  filters: FilterType[],
): string[] {
  const published = layout.defaultEntityIdentifiers ?? [];
  const entitiesFilter = filters.find((filter) => filter.key === "entities");
  const selected = Array.isArray(entitiesFilter?.value)
    ? entitiesFilter.value.map(String)
    : [];

  if (!selected.length) {
    return published;
  }

  const subset = selected.filter((identifier) =>
    published.includes(identifier),
  );
  return subset.length ? subset : published;
}

function sharedShellToReportLayoutWidget(
  shell: SharedViewWidgetShell,
): ReportLayoutWidget {
  return {
    id: shell.identifier ?? "",
    source: shell.source ?? undefined,
    presentation: (shell.presentation as PresentationKind | undefined) ?? undefined,
    spec: shell.spec ?? {},
    title: shell.title ?? undefined,
    defaultFilters: shell.defaultFilters ?? [],
  };
}

export function buildSharedWidgetFilterSet(
  widget: SharedViewWidgetShell,
  globalFilters: FilterType[],
  layout: SharedViewLayout,
  widgetFilters: FilterType[] = [],
): WidgetFilterSets {
  return buildWidgetFilterSet({
    widget: sharedShellToReportLayoutWidget(widget),
    globalFilters,
    layoutEntityIdentifiers: resolveSharedEntityIdentifiers(
      layout,
      globalFilters,
    ),
    widgetFilters,
  });
}
